import {useGetHealthSpecialtiesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthSpecialtiesQuery';
import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {useQueryParams} from 'presentation/pages';
import {getSpecialtyBySearch} from 'presentation/utils/GetSpecialtyBySearch';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useNavigate} from 'react-router-dom';
import {HealthBookingEnum} from 'presentation/constants';
import {MOST_SEARCHED} from 'presentation/modules/booking/shared/constants/mostSearched';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {FilterByLetterEnum} from 'presentation/modules/booking/shared/constants/filterByLetterEnum';
import {useMemo} from 'react';
import {textHandle} from 'presentation/utils/textHandle';
import {TelemedicineEnum} from 'presentation/modules/booking/shared/constants/telemedicineEnum';
import {sortByKey} from 'presentation/utils/sortByKey';

export function useHealthSpecialtyList() {
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const navigate = useNavigate();
	const {
		beneficiary: {idadeC},
	} = useAccountDataStore(state => state.accountData);

	const {
		clearSearch,
		SEARCH_PARAMS: {query, searchByLetter},
	} = useQueryParams();
	const {
		data: specialtiesList,
		isFetching: isFetchingSpecialtiesList,
		error: specialtiesListError,
		refetch: refetchSpecialtiesList,
	} = useGetHealthSpecialtiesQuery({isTelemedicine: false});

	const specialtiesListHasUnprocessableError =
		specialtiesListError &&
		specialtiesListError instanceof APIError &&
		specialtiesListError.statusCode === HttpStatusCode.unprocessable;

	const searchTerm = textHandle(query).sanitizeText();

	const searchFilteredByTerms = getSpecialtyBySearch({
		text: searchTerm,
		flowType: FlowEnum.HEALTH,
	});

	const FILTERED_LIST_BY_CATEGORY = specialtiesList?.map(specialty => ({
		...specialty,
		list: specialty?.list?.filter(item => {
			const formattedText = textHandle(item?.formattedText).sanitizeText();
			const termsRegex = new RegExp(`\\b${searchFilteredByTerms}\\b`, 'i');

			return formattedText.includes(searchTerm) || termsRegex.test(formattedText);
		}),
	}));

	const FILTERED_LIST_BY_LETTER = FILTERED_LIST_BY_CATEGORY?.filter(
		item => item?.category === searchByLetter,
	);

	const LIST = searchByLetter ? FILTERED_LIST_BY_LETTER : FILTERED_LIST_BY_CATEGORY;

	const showNotFoundState =
		!specialtiesList || specialtiesList.length === 0 || specialtiesListHasUnprocessableError;

	const showEmptyState = LIST?.every(categoryItem => categoryItem?.list?.length === 0);

	const allSpecialtiesAvailableFlatMap = specialtiesList?.flatMap(category => category?.list);

	const hasHeadacheSpecialty = Boolean(
		allSpecialtiesAvailableFlatMap?.some(
			specialty => specialty?.code === MOST_SEARCHED.headache.code,
		),
	);

	const shouldShowHeadacheInMostSearched =
		searchByLetter === FilterByLetterEnum.N && hasHeadacheSpecialty;

	const mostSearchedSpecialties = useMemo(() => {
		const allowedCategories: FilterByLetterEnum[] = [
			FilterByLetterEnum.C,
			FilterByLetterEnum.D,
			FilterByLetterEnum.O,
			FilterByLetterEnum.P,
			FilterByLetterEnum.R,
		];

		const isPediatric = idadeC < 18;
		const filteredSpecialties = specialtiesList
			?.filter(item => allowedCategories.includes(item?.category as FilterByLetterEnum))
			?.flatMap(category => category?.list || []);

		const existingCodes = new Set(filteredSpecialties?.map(specialty => specialty?.code));

		const matchedSpecialties = Object.values(MOST_SEARCHED)?.filter(specialty => {
			return (
				(isPediatric
					? specialty?.code === MOST_SEARCHED?.generalPediatric?.code
					: specialty?.code === MOST_SEARCHED?.generalAdult?.code) &&
				existingCodes.has(specialty?.code)
			);
		});

		const additionalSpecialties = Object.values(MOST_SEARCHED)?.filter(specialty =>
			existingCodes.has(specialty?.code),
		);

		if (shouldShowHeadacheInMostSearched) {
			additionalSpecialties.push(MOST_SEARCHED.headache);
		}

		const uniqueSpecialties = Array.from(
			new Map(
				[...(matchedSpecialties || []), ...(additionalSpecialties || [])].map(item => [
					item?.code,
					item,
				]),
			).values(),
		);

		const uniqueSpecialtiesWithDetailsInfo = uniqueSpecialties?.map(specialty => {
			const foundSpecialty = allSpecialtiesAvailableFlatMap?.find(
				s => s.code === specialty.code,
			);
			return {
				...specialty,
				hasDefinition: foundSpecialty?.hasDefinition,
				telemedicine: foundSpecialty?.telemedicine ?? TelemedicineEnum.NOT_TELEMEDICINE,
			};
		});

		const listSorted = sortByKey(uniqueSpecialtiesWithDetailsInfo, 'formattedText');

		return listSorted || [];
	}, [specialtiesList, idadeC]);

	const handleNavToStateSelection = () => {
		clearSearch();
		resetBookingData();
		navigate(HealthBookingEnum.HEALTH_BOOKING_UF);
	};

	return {
		isFetching: isFetchingSpecialtiesList,
		specialtiesListHasUnprocessableError,
		specialtiesListError,
		refetchSpecialtiesList,
		showEmptyState,
		showNotFoundState,
		handleNavToStateSelection,
		specialtiesList: LIST,
		mostSearchedSpecialties,
	};
}
